import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { auth, initialAuthReady } from '../lib/firebase';
import { setTenantTimezone } from '../lib/datetime';
import { setActiveTenant, getSelectedTenant } from '../lib/tenantSession';
import { tenantHeaders } from '../lib/tenantSession';

// Always get a fresh Firebase ID token; fall back to localStorage if no Firebase session.
const getToken = async () => {
  // Await Firebase's initial auth resolution first — the tenant by-brand bootstrap runs on mount, and a
  // synchronous currentUser read during hydration returns null, which strands the shell with no token
  // (empty tenant → the sidebar then falls back to the raw brand id). See P0-E/F/H.
  try { await initialAuthReady; } catch {}
  try {
    if (auth.currentUser) return await auth.currentUser.getIdToken();
  } catch {}
  return localStorage.getItem('crm_access_token') || '';
};

const BusinessContext = createContext();

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

// NOTE: Mock business data has been removed. Real tenants are now loaded from
// the backend (/api/billing/business/by-brand/<brandId>). For users that have not
// yet been associated to a tenant we render an empty placeholder shell.
const EMPTY_BUSINESS_SHELL = {
  brandId: null,
  businessName: '',
  businessEmail: '',
  businessPhone: '',
  sector: '',
  subSector: '',
  registrationNumber: '',
  logo: '/servisfy-logo.png',
  staff: [],
  conversations: [],
  analytics: {},
  tier_id: null,
  tier_limits: null,
  subscription_status: null,
  trial_end_at: null,
  grace_period_end_at: null,
  dunning_attempts: 0,
  dunning_max_attempts: 2,
  integrations: {},
};

export const BusinessProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [businessStaff, setBusinessStaff] = useState([]);

  // PMT1 active-tenant propagation. This context is where the active tenant is RESOLVED (login
  // payload, staff record, or a verified switch), so it is where the resolved value is published to
  // the tenantSession authority the shared fetch helpers read. Without this the header was empty on
  // any session that did not arrive via switchTenant, and every /me/* commercial call failed closed
  // with 409. SUPERADMIN is a portal shell, not a tenant, so it is never published.
  useEffect(() => {
    const brandId = currentBusiness?.brandId;
    if (brandId && brandId !== 'SUPERADMIN') setActiveTenant(brandId);
  }, [currentBusiness?.brandId]);

  const _fetchStaffForBrand = async (brandId) => {
    if (!brandId) return [];
    try {
      const token = await getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/by-brand/${brandId}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.staff || [];
    } catch (e) {
      console.error('Error loading staff roster:', e);
      return [];
    }
  };

  const fetchBusinessStaff = async (brandId) => {
    const bid = brandId || currentBusiness?.brandId;
    const staff = await _fetchStaffForBrand(bid);
    setBusinessStaff(staff);
    return staff;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user || user.role === 'customer') {
        setCurrentBusiness(null);
        setBusinessData(null);
        setBusinessStaff([]);
        return;
      }

      // PMT1 cache isolation: drop the PREVIOUS tenant's projection before resolving the new one, so
      // a tenant change can never render Tenant A's business profile or staff roster while Tenant B
      // is loading. Without this the stale object survives until the fetch resolves.
      setCurrentBusiness(null);
      setBusinessData(null);
      setBusinessStaff([]);

      // Super Admin uses its own portal context; main CRM doesn't need to fetch a tenant
      if (user.role === 'super_admin') {
        const superAdminBusiness = {
          ...EMPTY_BUSINESS_SHELL,
          brandId: 'SUPERADMIN',
          businessName: 'Servisfy — Super Administrator',
          businessEmail: 'admin@servisfy.com',
        };
        setCurrentBusiness(superAdminBusiness);
        setBusinessData(superAdminBusiness);
        return;
      }

      // Accept snake_case from the login payload; if still missing, resolve via the
      // staff record (phone → brand_id) so tenant identity (ID) is never left blank.
      // The SELECTED tenant wins over whatever the login payload happened to carry: after a tenant
      // switch the selection is the tenant the UI is actually displaying, while the login payload
      // still names the tenant the session started in.
      let brandId = getSelectedTenant() || user.brandId || user.brand_id || null;
      if (!brandId) {
        // The login payload does not always carry a phone (e.g. Director/IT logging in
        // via Firebase phone-OTP). Fall back to the authenticated Firebase user's phone
        // number so tenant identity resolves for ALL staff, not just those whose payload
        // included a phone. Without this, Director/IT render a blank Business/Brand ID.
        let phone = user.phone || user.phoneNumber || null;
        if (!phone) { try { phone = auth.currentUser?.phoneNumber || null; } catch {} }
        if (phone) {
          try {
            const r = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/by-phone/${encodeURIComponent(phone)}`
            );
            if (r.ok) {
              const s = await r.json();
              // Accept brand_id, falling back to business_id (staff docs may store either).
              if (s && s.linked) brandId = s.brand_id || s.business_id || null;
            }
          } catch {}
        }
      }
      // Publish BEFORE hydration. The active-tenant header must not depend on a tenant fetch
      // succeeding: every tenant-sensitive request that has no path tenant needs it, and several of
      // them run concurrently with (or ahead of) this hydration. Previously the header was published
      // only from the hydrated `currentBusiness`, so any path that failed, returned an empty body, or
      // simply had not resolved yet left the header empty and those requests failed closed with 409.
      if (brandId && brandId !== 'SUPERADMIN') setActiveTenant(brandId);

      if (!brandId) {
        // No tenant linked yet → render an empty shell. UI will guide signup/upgrade.
        setCurrentBusiness({ ...EMPTY_BUSINESS_SHELL });
        setBusinessData({ ...EMPTY_BUSINESS_SHELL });
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          setCurrentBusiness({ ...EMPTY_BUSINESS_SHELL, brandId });
          setBusinessData({ ...EMPTY_BUSINESS_SHELL, brandId });
          return;
        }
        // Resilient tenant lookup — a transient transport blip (P0-I) must NOT strand the shell without a
        // business name (which would surface the raw brand id). Retry with short backoff, then fall back.
        let t = null;
        for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
          try {
            const res = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/business/by-brand/${brandId}`,
              { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
            );
            if (!res.ok) throw new Error(`tenant lookup failed (${res.status})`);
            t = await res.json();
            break;
          } catch (err) {
            if (attempt === 2) throw err;
            await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
          }
        }
        if (cancelled) return;
        if (!t) {
          // 200 with an empty body: keep the tenant we already know rather than stranding the shell
          // (and the header) with nothing.
          setCurrentBusiness({ ...EMPTY_BUSINESS_SHELL, brandId });
          setBusinessData({ ...EMPTY_BUSINESS_SHELL, brandId });
          return;
        }

        const business = {
          ...EMPTY_BUSINESS_SHELL,
          brandId: t.brand_id || brandId,   // never lose the tenant we resolved to get here
          businessName: t.business_name,
          businessEmail: t.admin_email || '',
          businessPhone: t.business_phone || '',
          sector: t.sector || '',
          subSector: t.sub_sector || '',
          registrationNumber: t.registration_number || '',
          tier_id: t.tier_id,
          tier_limits: t.tier_limits || {},
          subscription_status: t.subscription_status,
          trial_end_at: t.trial_end_at,
          grace_period_end_at: t.grace_period_end_at,
          dunning_attempts: t.dunning_attempts || 0,
          dunning_max_attempts: t.dunning_max_attempts || 2,
          integrations: t.integrations || {},
          timezone: (t.settings && t.settings.timezone) || null,
        };
        setCurrentBusiness(business);
        setBusinessData(business);
        setTenantTimezone(business.timezone);  // B6A-3: drive tenant-tz date formatting

        _fetchStaffForBrand(business.brandId).then(staff => {
          if (!cancelled) setBusinessStaff(staff);
        }).catch(() => {});
      } catch (e) {
        // Fall back to empty shell — no mock data
        if (cancelled) return;
        setCurrentBusiness({ ...EMPTY_BUSINESS_SHELL, brandId });
        setBusinessData({ ...EMPTY_BUSINESS_SHELL, brandId });
      }
    };
    load();
    return () => { cancelled = true; };
    // PMT1: brandId is part of the key — selecting/switching tenant must re-resolve the projection.
  }, [user, user?.brandId, user?.brand_id]);

  // Business-specific data access methods
  const getBusinessConversations = async () => {
    if (!currentBusiness?.brandId) return [];

    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/v1/conversations/business/${currentBusiness.brandId}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );

      if (!response.ok) throw new Error('Failed to fetch conversations');
      const data = await response.json();
      return data.conversations || [];
    } catch (error) {
      console.error('Error fetching business conversations:', error);
      return currentBusiness?.conversations || [];
    }
  };

  // Wired to the existing tenant-scoped endpoint GET /api/v1/analytics/business/{brandId}.
  // Backend enforces staff membership on the caller's own brand_id (tenant isolation).
  // On any failure, return {} and log — never fabricate analytics.
  const getBusinessAnalytics = async (period = '7d') => {
    if (!currentBusiness?.brandId) return {};

    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/v1/analytics/business/${currentBusiness.brandId}?period=${encodeURIComponent(period)}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );

      if (!response.ok) throw new Error('Failed to fetch business analytics');
      return await response.json();
    } catch (error) {
      console.error('Error fetching business analytics:', error);
      return {};
    }
  };

  const getBusinessStaff = () => {
    return businessStaff;
  };

  const updateStaffRole = async (staffId, newRole) => {
    try {
      const token = await getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/${staffId}/role`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
          body: JSON.stringify({ role: newRole }),
        }
      );
      if (!res.ok) throw new Error('Role update failed');
      if (currentBusiness?.brandId) {
        const updated = await _fetchStaffForBrand(currentBusiness.brandId);
        setBusinessStaff(updated);
      }
    } catch (error) {
      console.error('Error updating staff role:', error);
      throw error;
    }
  };

  const removeStaffMember = async (staffId) => {
    const removed = businessStaff.find(m => m.id === staffId);
    try {
      const token = await getToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/${staffId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      if (!res.ok) throw new Error('Staff removal failed');
      if (currentBusiness?.brandId) {
        const updated = await _fetchStaffForBrand(currentBusiness.brandId);
        setBusinessStaff(updated);
      }
    } catch (error) {
      console.error('Error removing staff member:', error);
      throw error;
    }
    return removed;
  };

  const addStaffMember = (staffData) => {
    if (!currentBusiness) return;
    
    const newStaff = {
      id: `${currentBusiness.brandId.toLowerCase()}-${Date.now()}`,
      ...staffData,
      role: staffData.role || 'operator', // Default to operator if no role assigned by admin
      joinDate: new Date().toISOString(),
      status: 'invited',
      invitedBy: 'admin', // Track who invited the staff
      businessId: currentBusiness.brandId,
      accessLevel: getAccessLevel(staffData.role || 'operator')
    };
    
    setCurrentBusiness(prev => ({
      ...prev,
      staff: [...prev.staff, newStaff]
    }));
    
    // In a real app, send invitation email here
    console.log(`Invitation sent to ${staffData.email} for role: ${newStaff.role}`);
    
    return newStaff;
  };

  // Helper function to determine access level based on role
  const getAccessLevel = (role) => {
    const accessLevels = {
      'operator': ['conversations', 'tickets', 'solution_bank'],
      'manager': ['conversations', 'tickets', 'solution_bank', 'analytics', 'staff_view', 'crm'],
      'analyst': ['analytics', 'reports', 'solution_bank'],
      'admin': ['all'], // Full access except super_admin functions
      'super_admin': ['global_admin'] // Global platform access
    };
    
    return accessLevels[role] || accessLevels['operator'];
  };

  // Function to revoke business access (for when staff is removed)
  const revokeBusinessAccess = async (staffEmail, businessId) => {
    try {
      // In a real app, this would call the backend to:
      // 1. Remove the user from the business
      // 2. Revoke all business-specific permissions
      // 3. Convert user role back to 'customer'
      // 4. Clear any cached business data
      
      console.log(`Revoking business access for ${staffEmail} from business ${businessId}`);
      
      // Mock API call
      /*
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/businesses/${businessId}/staff/${staffEmail}/revoke`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      */
      
      return { success: true, convertedToCustomer: true };
      
    } catch (error) {
      console.error('Error revoking business access:', error);
      return { success: false, error: error.message };
    }
  };

  // Solution management functions
  const getSolutions = async (category = null) => {
    if (!currentBusiness?.brandId) return [];

    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = `${backendUrl}/api/v1/solutions/${currentBusiness.brandId}${category && category !== 'all' ? `?category=${category}` : ''}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch solutions');
      }

      const data = await response.json();
      return data.solutions || [];
    } catch (error) {
      console.error('Error fetching solutions:', error);
      return [];
    }
  };

  const createSolution = async (solutionData) => {
    if (!currentBusiness?.brandId) return null;

    try {
      const token = await getToken();
      const newSolution = {
        id: `sol_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...solutionData,
        business_id: currentBusiness.brandId,
        // `created_by` and `status` are SERVER-owned and deliberately not sent.
        //  - created_by: this passed `user.email`, which for a phone-authenticated account is a phone
        //    NUMBER — a customer-grade identifier written into a governed knowledge record. The
        //    backend stamps the tenant-scoped staff id it already resolves for `submitted_by`.
        //  - status: this asked for 'approved' for any non-operator, i.e. a client requesting its own
        //    approval. The backend has always forced 'pending_approval' and separation of duties, so
        //    nothing changes behaviourally — but a client should not be seen to ask.
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        usage_count: 0,
        success_rate: 100,
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/v1/solutions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newSolution),
      });

      if (!response.ok) throw new Error('Failed to create solution');
      return await response.json();
    } catch (error) {
      console.error('Error creating solution:', error);
      throw error;
    }
  };

  const updateSolution = async (solutionId, solutionData) => {
    if (!currentBusiness) return null;

    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const solution = { ...solutionData, business_id: currentBusiness.brandId };

      const response = await fetch(`${backendUrl}/api/v1/solutions/${solutionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, ...tenantHeaders(),
        },
        body: JSON.stringify(solution),
      });

      if (!response.ok) throw new Error('Failed to update solution');
      return await response.json();
    } catch (error) {
      console.error('Error updating solution:', error);
      throw error;
    }
  };

  const deleteSolution = async (solutionId) => {
    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/v1/solutions/${solutionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });

      if (!response.ok) throw new Error('Failed to delete solution');
      return await response.json();
    } catch (error) {
      console.error('Error deleting solution:', error);
      throw error;
    }
  };

  const importSolutions = async (csvContent) => {
    if (!currentBusiness?.brandId) return null;

    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/v1/solutions/import?business_id=${currentBusiness.brandId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, ...tenantHeaders(),
          },
          body: JSON.stringify({ file_content: csvContent }),
        }
      );

      if (!response.ok) throw new Error('Failed to import solutions');
      return await response.json();
    } catch (error) {
      console.error('Error importing solutions:', error);
      throw error;
    }
  };

  const exportSolutions = async (format = 'csv') => {
    if (!currentBusiness?.brandId) return null;

    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/v1/solutions/${currentBusiness.brandId}/export?format=${format}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );

      if (!response.ok) throw new Error('Failed to export solutions');
      return await response.json();
    } catch (error) {
      console.error('Error exporting solutions:', error);
      throw error;
    }
  };

  const generatePerformanceReport = async () => {
    if (!currentBusiness?.brandId) return null;

    try {
      const token = await getToken();
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(
        `${backendUrl}/api/v1/solutions/${currentBusiness.brandId}/performance-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, ...tenantHeaders(),
          },
        }
      );

      if (!response.ok) throw new Error('Failed to generate performance report');
      return await response.json();
    } catch (error) {
      console.error('Error generating performance report:', error);
      throw error;
    }
  };

  const value = {
    currentBusiness,
    businessData,
    businessStaff,
    fetchBusinessStaff,
    getBusinessConversations,
    getBusinessAnalytics,
    getBusinessStaff,
    updateStaffRole,
    removeStaffMember,
    addStaffMember,
    // Solution management functions
    getSolutions,
    createSolution,
    updateSolution,
    deleteSolution,
    importSolutions,
    exportSolutions,
    generatePerformanceReport
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};